import { useState } from 'react';
import { format } from 'date-fns';

import { Accordion, Box, Icon, Menu, Table, Tag, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconMailFilled, IconPhoneFilled } from '@zillow/constellation-icons';

import {
  ModalDeleteTenancyPeriod,
  ModalDeleteTenant,
  ModalEditTenantForm,
  ModalFormFieldLeaseTerm,
} from '@zg-rentals/modal-tenant';
import { useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { getTenantStateTag, getValueOrDash } from './utils';

import { singleTextLineStyles } from '../../../../styles/text';
import { columnContentMaxWidth, columnEmailBox, iconRowItem, infoCol, infoRow, sectionTitleRow } from './styles';

import {
  emitTenancyPeriodEditClickEvent,
  emitTenantAccordionItemAddTenancyPeriodClickEvent,
  emitTenantAccordionItemEditClickEvent,
  TENANCY_PERIOD_EDIT_CLICK_EVENT_ID,
  TENANT_ACCORDION_ITEM_ADD_TENANCY_PERIOD_CLICK_EVENT_ID,
  TENANT_DIRECTORY_EDIT_CLICK_EVENT_ID,
} from '../../../../analytics/events/tenantDirectory';
import { client } from '../../../../http/zgApiClient';
import { useAuth } from '../../../AuthProvider';

import type { RenterContactTenant } from '@zg-rentals/modal-tenant';
import type {
  RentalManagerGetTenantsRenterContactTenancyDto,
  RentalManagerRenterContactTenantWithTenanciesDto,
  RentalManagerRenterUpdateRenterResponseDto,
  RenterInfoRenterAddedDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { DatarouterError } from '@zg-rentals/web-base';

type TenantAccordionItemProps = {
  listingAlias: string;
  tenant: RentalManagerRenterContactTenantWithTenanciesDto;
  index: number;
  isArchived: boolean;
  onDeleteTenancyPeriodSuccess?: () => void | null;
  onDeleteTenantSuccess?: () => void | null;
  onDeleteTenancyPeriodError?: (error: DatarouterError) => void | null;
  onDeleteTenantError?: (error: DatarouterError) => void | null;
  onEditTenantSuccess?: (updatedRenterDto?: RentalManagerRenterUpdateRenterResponseDto) => void | null;
  onLeaseTermActionSuccess?: ({
    addedRenters,
    isEditMode,
  }: {
    addedRenters?: Array<RenterInfoRenterAddedDto>;
    isEditMode: boolean;
  }) => void | null;
  onEditTenantError?: (error: DatarouterError) => void | null;
  onLeaseTermActionError?: (error: DatarouterError) => void | null;
  onRequestProofSuccess?: (message: string) => void | null;
  onRequestProofError?: (message: string) => void | null;
};

const TenantAccordionItem = ({
  listingAlias,
  tenant,
  index,
  isArchived,
  onDeleteTenancyPeriodSuccess,
  onDeleteTenancyPeriodError,
  onDeleteTenantError,
  onDeleteTenantSuccess,
  onEditTenantSuccess,
  onLeaseTermActionSuccess,
  onEditTenantError,
  onLeaseTermActionError,
}: TenantAccordionItemProps) => {
  const { enqueueToast } = useToast();
  const { user } = useAuth();

  const [isModalRemoveTenantOpen, setIsModalRemoveTenantOpen] = useState(false);

  const [modalDeleteTenancyPeriodSettings, setModalDeleteTenancyPeriodSettings] = useState<{
    isOpen: boolean;
    selectedTenancyPeriod?: RentalManagerGetTenantsRenterContactTenancyDto | null;
  }>({
    isOpen: false,
    selectedTenancyPeriod: null,
  });

  const [modalFormFieldLeaseTermSettings, setModalFormFieldLeaseTermSettings] = useState<{
    isOpen: boolean;
    isEditMode: boolean;
    selectedTenancyPeriod?: RentalManagerGetTenantsRenterContactTenancyDto | null;
  }>({
    isOpen: false,
    isEditMode: true,
    selectedTenancyPeriod: null,
  });

  const [isModalEditTenantOpen, setIsModalEditTenantOpen] = useState(false);

  const { data: webAnalyticsDataWithLease } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: true,
    },
    {
      enabled: Boolean(listingAlias),
    },
  );

  const currentProperty = { listingAlias };

  const copyEmail = async () => {
    if (!tenant.emailAddress) {
      return;
    }

    try {
      await navigator.clipboard.writeText(tenant.emailAddress!);

      enqueueToast(<Toast tone="success">Email copied to clipboard</Toast>);
    } catch {
      enqueueToast(<Toast tone="critical">Email couldn’t be copied to clipboard</Toast>);
    }
  };

  const triggerCSEventEditTenantClick = () => {
    if (user) {
      emitTenantAccordionItemEditClickEvent({ user, currentProperty });
    }
  };

  const handleEditTenantClick = () => {
    setIsModalEditTenantOpen(true);
    triggerCSEventEditTenantClick();
  };

  const handleEditTenancyPeriodClick = (tenancyPeriod?: RentalManagerGetTenantsRenterContactTenancyDto) => {
    setModalFormFieldLeaseTermSettings({
      isOpen: true,
      isEditMode: true,
      selectedTenancyPeriod: tenancyPeriod,
    });

    if (webAnalyticsDataWithLease && user) {
      const { lease, ...webAnalyticsData } = webAnalyticsDataWithLease;

      emitTenancyPeriodEditClickEvent({
        currentProperty: webAnalyticsData ?? currentProperty,
        lease,
        user,
      });
    }
  };

  const handleDeleteTenancyPeriodClick = (tenancyPeriod?: RentalManagerGetTenantsRenterContactTenancyDto) => {
    setModalDeleteTenancyPeriodSettings({
      isOpen: true,
      selectedTenancyPeriod: tenancyPeriod,
    });
  };

  const handleRemoveTenantClick = () => {
    setIsModalRemoveTenantOpen(true);
  };

  const handleAddTenancyPeriodClick = () => {
    setModalFormFieldLeaseTermSettings({
      isOpen: true,
      isEditMode: false,
    });

    if (webAnalyticsDataWithLease && user) {
      const { lease, ...webAnalyticsData } = webAnalyticsDataWithLease;

      emitTenantAccordionItemAddTenancyPeriodClickEvent({
        currentProperty: webAnalyticsData ?? currentProperty,
        lease,
        user,
      });
    }
  };

  const renderContactInfo = (tenant: RentalManagerRenterContactTenantWithTenanciesDto) => (
    <Box data-testid="InfoRowContact" css={infoRow}>
      <Box css={{ ...infoCol }} data-testid="InfoRowContactItem">
        <Icon role="img" size="sm" css={{ ...iconRowItem }}>
          <IconMailFilled />
        </Icon>

        {getValueOrDash(tenant.emailAddress) === '-' ? (
          '-'
        ) : (
          <Box css={columnEmailBox}>
            <Text
              className="fs-mask"
              data-testid="EmailAddress"
              color="text.neutral"
              textStyle="body"
              css={{
                ...columnContentMaxWidth,
                ...singleTextLineStyles,
              }}
            >
              {tenant.emailAddress}
            </Text>

            <TextButton size="sm" textStyle="body-sm" onClick={copyEmail}>
              Copy email
            </TextButton>
          </Box>
        )}
      </Box>

      <Box css={infoCol}>
        <Icon role="img" size="sm" css={iconRowItem}>
          <IconPhoneFilled />
        </Icon>

        <Text className="fs-mask" textStyle="body">
          {getValueOrDash(tenant.phoneNumber)}
        </Text>
      </Box>
    </Box>
  );

  const renderTenancyPeriodItemList = () => {
    return tenant.tenancies?.map((tenancyPeriod: RentalManagerGetTenantsRenterContactTenancyDto) => {
      return (
        <Table.Row key={tenancyPeriod.renterRoleId}>
          <Table.Cell>{format(new Date(tenancyPeriod.startDateMs), 'MM/dd/yy')}</Table.Cell>
          <Table.Cell>
            {tenancyPeriod.endDateMs ? format(new Date(tenancyPeriod.endDateMs), 'MM/dd/yy') : '-'}
          </Table.Cell>
          <Table.Cell>
            <Tag size="sm" tone="gray">
              {getTenantStateTag(tenancyPeriod.state)}
            </Tag>
          </Table.Cell>
          {!isArchived && (
            <Table.Cell>
              <Menu
                content={
                  <>
                    <Menu.Item>
                      <Menu.ItemLabel
                        data-eventid={TENANCY_PERIOD_EDIT_CLICK_EVENT_ID}
                        onClick={() => handleEditTenancyPeriodClick(tenancyPeriod)}
                      >
                        Edit
                      </Menu.ItemLabel>
                    </Menu.Item>
                    <Menu.Item>
                      <Menu.ItemLabel onClick={() => handleDeleteTenancyPeriodClick(tenancyPeriod)}>
                        Delete
                      </Menu.ItemLabel>
                    </Menu.Item>
                  </>
                }
                shouldAwaitInteractionResponse
              />
            </Table.Cell>
          )}
        </Table.Row>
      );
    });
  };

  const transposeFromContactWithTenanciesToRenterContactDto = (
    renter: RentalManagerRenterContactTenantWithTenanciesDto,
    renterRoleId?: string,
  ): RenterContactTenant => {
    const defaultTenancy = {
      renterRoleId: '',
      startDateMs: 0,
      state: '',
      endDateMs: 0,
    };

    const displayTenancy = renterRoleId
      ? (renter.tenancies?.find((tenancy) => tenancy.renterRoleId === renterRoleId) ?? defaultTenancy)
      : defaultTenancy;

    const { tenancies: _, ...renterWithoutTenancies } = renter;

    return {
      ...renterWithoutTenancies,
      displayTenancy,
    };
  };

  return (
    <>
      <Accordion.Item value={`tenant-${index}`} key={tenant.renterId}>
        <Accordion.Header>
          <Text className="fs-mask" textStyle="body-bold">
            {tenant.fullName}
          </Text>
        </Accordion.Header>

        <Accordion.Panel>
          <Box
            data-testid="AccordionContainer"
            className="fs-unmask"
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: '{spacing.600}' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change */,
            }}
          >
            <Box data-testid="ContactInfoContainer">
              <Box css={{ display: 'flex', justifyContent: 'space-between', ...sectionTitleRow }}>
                <Text textStyle="body-bold" color="text.neutral">
                  Contact information
                </Text>

                {!isArchived && (
                  <TextButton
                    data-eventid={TENANT_DIRECTORY_EDIT_CLICK_EVENT_ID}
                    size="sm"
                    textStyle="body-sm-bold"
                    tone="brand"
                    onImpact={false}
                    onClick={handleEditTenantClick}
                  >
                    Edit
                  </TextButton>
                )}
              </Box>

              <Box
                data-testid="InfoContentContainer"
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '{spacing.400}' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change */,
                }}
              >
                {renderContactInfo(tenant)}
              </Box>
            </Box>

            {tenant.tenancies && (
              <Box data-testid="TenancyPeriodContainer">
                <Box css={{ display: 'flex', justifyContent: 'space-between', ...sectionTitleRow }}>
                  <Text textStyle="body-bold" color="text.neutral">
                    Tenancy period
                  </Text>
                  {!isArchived && (
                    <TextButton
                      data-eventid={TENANT_ACCORDION_ITEM_ADD_TENANCY_PERIOD_CLICK_EVENT_ID}
                      size="sm"
                      textStyle="body-sm-bold"
                      tone="brand"
                      onImpact={false}
                      onClick={handleAddTenancyPeriodClick}
                    >
                      Add
                    </TextButton>
                  )}
                </Box>

                <Box
                  data-testid="TenancyPeriodContentContainer"
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '{spacing.400}' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change */,
                  }}
                >
                  <Table.Responsive
                    data-testid="TenancyPeriodTable"
                    css={{
                      width: '100%',
                    }}
                  >
                    <Table.Root
                      alignments={isArchived ? ['start', 'start', 'start'] : ['start', 'start', 'start', 'start']}
                      appearance="horizontal"
                      aria-label="Tenancy period table"
                      size="md"
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Start date</Table.HeaderCell>
                          <Table.HeaderCell>End date</Table.HeaderCell>
                          <Table.HeaderCell>Status</Table.HeaderCell>
                          {/* overflow menu column */}
                          {!isArchived && <Table.HeaderCell>&nbsp;</Table.HeaderCell>}
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>{renderTenancyPeriodItemList()}</Table.Body>
                    </Table.Root>
                  </Table.Responsive>

                  {!isArchived && (
                    <TextButton
                      css={{
                        alignSelf: 'flex-end',
                        marginTop:
                          '{spacing.200}' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change */,
                      }}
                      size="md"
                      tone="brand"
                      // TODO: add remove tenant endpoint to remove all tenancies once complete
                      onClick={handleRemoveTenantClick}
                    >
                      Remove tenant
                    </TextButton>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Accordion.Panel>
      </Accordion.Item>

      {/* check isOpen to pass correct renter data */}
      {modalFormFieldLeaseTermSettings.isOpen && (
        <ModalFormFieldLeaseTerm
          client={client}
          isOpen={modalFormFieldLeaseTermSettings.isOpen}
          isEditMode={modalFormFieldLeaseTermSettings.isEditMode}
          listingAlias={listingAlias}
          onClose={() => {
            setModalFormFieldLeaseTermSettings({
              isOpen: false,
              isEditMode: true,
              selectedTenancyPeriod: null,
            });
          }}
          renter={
            modalFormFieldLeaseTermSettings.isEditMode
              ? transposeFromContactWithTenanciesToRenterContactDto(
                  tenant,
                  modalFormFieldLeaseTermSettings.selectedTenancyPeriod?.renterRoleId,
                )
              : transposeFromContactWithTenanciesToRenterContactDto(tenant)
          }
          onSuccess={onLeaseTermActionSuccess}
          onError={onLeaseTermActionError}
        />
      )}

      <ModalEditTenantForm
        client={client}
        isOpen={isModalEditTenantOpen}
        listingAlias={listingAlias}
        onClose={() => {
          setIsModalEditTenantOpen(false);
        }}
        renter={transposeFromContactWithTenanciesToRenterContactDto(tenant)}
        onSuccess={onEditTenantSuccess}
        onError={onEditTenantError}
      />

      {/* check isOpen to pass correct renter data */}
      {modalDeleteTenancyPeriodSettings.isOpen && (
        <ModalDeleteTenancyPeriod
          client={client}
          isOpen={modalDeleteTenancyPeriodSettings.isOpen}
          onClose={() => setModalDeleteTenancyPeriodSettings({ isOpen: false, selectedTenancyPeriod: null })}
          renter={transposeFromContactWithTenanciesToRenterContactDto(
            tenant,
            modalDeleteTenancyPeriodSettings.selectedTenancyPeriod?.renterRoleId,
          )}
          onSuccess={onDeleteTenancyPeriodSuccess}
          onError={onDeleteTenancyPeriodError}
        />
      )}

      {isModalRemoveTenantOpen && (
        <ModalDeleteTenant
          client={client}
          isOpen={isModalRemoveTenantOpen}
          onClose={() => setIsModalRemoveTenantOpen(false)}
          renterName={tenant.fullName ?? ''}
          renterRoleIdsToDelete={tenant.tenancies?.map((tenancy) => tenancy.renterRoleId) ?? []}
          onSuccess={onDeleteTenantSuccess}
          onError={onDeleteTenantError}
        />
      )}
    </>
  );
};

export default TenantAccordionItem;
